import {
  normaliseRecipeManualState,
  type RecipeManualState,
} from "./recipeManualHistory";

type RecipeStorage = Pick<Storage, "getItem" | "setItem">;

export interface LocalRecipe {
  id: string;
  name: string;
  settings: RecipeManualState["settings"];
  keepRecipeShape: boolean;
  lastRecipeId: RecipeManualState["lastRecipeId"];
  localRecipeId: string;
  createdAt: number;
}

export const LOCAL_RECIPES_STORAGE_KEY = "td3-junkmem.localRecipes.v1";
export const LOCAL_RECIPES_STORAGE_VERSION = 1;
export const LOCAL_RECIPES_LIMIT = 48;

function browserStorage(): RecipeStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function cleanName(raw: unknown): string {
  const name = typeof raw === "string" ? raw : "";
  return name.replace(/\s+/g, " ").trim().slice(0, 48) || "Local recipe";
}

function cleanId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const id = raw.trim().slice(0, 80);
  return id ? id : null;
}

function cleanCreatedAt(raw: unknown): number {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : Date.now();
}

function normaliseLocalRecipe(raw: unknown): LocalRecipe | null {
  if (!isRecord(raw) || !isRecord(raw.settings)) return null;
  const id = cleanId(raw.id);
  if (!id) return null;

  const state = normaliseRecipeManualState({
    settings: raw.settings as unknown as RecipeManualState["settings"],
    keepRecipeShape: Boolean(raw.keepRecipeShape),
    lastRecipeId: raw.lastRecipeId as RecipeManualState["lastRecipeId"],
    localRecipeId: id,
  });

  return {
    id,
    name: cleanName(raw.name),
    settings: state.settings,
    keepRecipeShape: state.keepRecipeShape,
    lastRecipeId: state.lastRecipeId,
    localRecipeId: id,
    createdAt: cleanCreatedAt(raw.createdAt),
  };
}

export function loadLocalRecipes(
  storage: RecipeStorage | null = browserStorage(),
): LocalRecipe[] {
  if (!storage) return [];

  try {
    const raw = storage.getItem(LOCAL_RECIPES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    const recipes =
      isRecord(parsed) &&
      parsed.version === LOCAL_RECIPES_STORAGE_VERSION &&
      Array.isArray(parsed.recipes)
        ? parsed.recipes
        : Array.isArray(parsed)
          ? parsed
          : [];
    const seen = new Set<string>();
    return recipes
      .map(normaliseLocalRecipe)
      .filter((recipe): recipe is LocalRecipe => {
        if (!recipe || seen.has(recipe.id)) return false;
        seen.add(recipe.id);
        return true;
      })
      .slice(0, LOCAL_RECIPES_LIMIT);
  } catch {
    return [];
  }
}

export function saveLocalRecipes(
  recipes: LocalRecipe[],
  storage: RecipeStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;

  try {
    const safeRecipes = recipes.slice(0, LOCAL_RECIPES_LIMIT).map((recipe) => ({
      id: recipe.id,
      name: cleanName(recipe.name),
      settings: normaliseRecipeManualState(recipe).settings,
      keepRecipeShape: Boolean(recipe.keepRecipeShape),
      lastRecipeId: recipe.lastRecipeId,
      localRecipeId: recipe.id,
      createdAt: cleanCreatedAt(recipe.createdAt),
    }));
    storage.setItem(
      LOCAL_RECIPES_STORAGE_KEY,
      JSON.stringify({
        version: LOCAL_RECIPES_STORAGE_VERSION,
        recipes: safeRecipes,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export function suggestLocalRecipeName(
  existing: LocalRecipe[],
  currentRecipeName: string,
): string {
  const base = cleanName(currentRecipeName.replace(/\s+\d+$/, ""));
  const used = new Set(existing.map((recipe) => recipe.name));
  if (!used.has(base)) return base;

  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${base} ${index}`;
    if (!used.has(candidate)) return candidate;
  }

  return `${base} ${existing.length + 1}`;
}

export function createLocalRecipeFromState(
  state: RecipeManualState,
  name: string,
  now = Date.now(),
  random = Math.random,
): LocalRecipe {
  const id = `local-${now.toString(36)}-${Math.floor(
    random() * Number.MAX_SAFE_INTEGER,
  ).toString(36)}`;
  const safeState = normaliseRecipeManualState({
    ...state,
    localRecipeId: id,
  });

  return {
    id,
    name: cleanName(name),
    settings: safeState.settings,
    keepRecipeShape: safeState.keepRecipeShape,
    lastRecipeId: safeState.lastRecipeId,
    localRecipeId: id,
    createdAt: now,
  };
}

export function localRecipeMatchesState(
  recipe: LocalRecipe,
  state: RecipeManualState,
): boolean {
  const safeRecipe = normaliseLocalRecipe(recipe);
  if (!safeRecipe) return false;
  return (
    safeRecipe.keepRecipeShape === state.keepRecipeShape &&
    safeRecipe.lastRecipeId === state.lastRecipeId &&
    safeRecipe.settings.presetId === state.settings.presetId &&
    safeRecipe.settings.lengthMin === state.settings.lengthMin &&
    safeRecipe.settings.lengthMax === state.settings.lengthMax &&
    safeRecipe.settings.tripletChance === state.settings.tripletChance &&
    safeRecipe.settings.accentChance === state.settings.accentChance &&
    safeRecipe.settings.slideChance === state.settings.slideChance &&
    safeRecipe.settings.tieChance === state.settings.tieChance &&
    safeRecipe.settings.restChance === state.settings.restChance &&
    safeRecipe.settings.pitchMin === state.settings.pitchMin &&
    safeRecipe.settings.pitchMax === state.settings.pitchMax &&
    safeRecipe.settings.scaleId === state.settings.scaleId &&
    safeRecipe.settings.root === state.settings.root &&
    safeRecipe.settings.enforceAudibleStep === state.settings.enforceAudibleStep &&
    safeRecipe.settings.allowTieOnFirstStep === state.settings.allowTieOnFirstStep
  );
}
